<template>
  <main class="create-team-page">
    <CreateTeamModal v-if="newTeam" v-model="showModal" :team="newTeam" />
    <div class="container maxWidth-sm">
      <h1 class="text-center">Create a New Team</h1>
      <div class="create-team-section">
        <div class="section-title">
          <StepNumber width="40px">1</StepNumber>
          <h2>Team Lead</h2>
        </div>
        <p class="team-create-description">
          As you’re creating this team, you are automatically assigned Team Lead - a powerful role!
          You will learn more about the Team Lead role once your team is set up.
        </p>
      </div>
      <div class="create-team-section">
        <div class="section-title">
          <StepNumber width="40px">2</StepNumber>
          <h2>Choose a name</h2>
        </div>
        <div style="flex-basis: 70%" :class="{ 'has-error': v$.name.$error }">
          <input
            type="text"
            maxlength="80"
            aria-label="Team name"
            class="team-input"
            placeholder="Enter a name for your team"
            v-model="name"
            @blur="v$.name.$touch()"
            data-test="team-name-input"
          />
          <small class="error-feedback text-danger" data-test="team-input-error">
            Please provide a name for your team<span v-if="!v$.name.regex">
              (without special characters)</span
            ></small
          >
          <div class="invalid-feedback"></div>
        </div>
        <div v-if="userTaggedTeams && userTaggedTeams.length" class="tagged-teams-container">
          <InfoIcon
            icon-width="20"
            tooltip-text="Suggestions based off Foundations pathway"
            name="team-suggestion"
          />
          <div class="data-tags scrollbar-none">
            <DataTag
              v-for="team in userTaggedTeams"
              :key="team"
              :is-selected="name === team"
              @on-tag-click="name = team"
              data-test="team-suggestion-tag"
              >{{ team }}
            </DataTag>
          </div>
        </div>
      </div>
      <div class="create-team-section">
        <div class="section-title">
          <StepNumber width="40px">3</StepNumber>
          <h2>Choose an image</h2>
        </div>
        <div v-if="imageList" class="image-grid">
          <div class="create-team-image">
            <ImageUploader
              :is-active="customImageIsSelected"
              @file-uploaded="onFileUpload"
              @tile-selected="selectCustomImage"
              ref="imageUploader"
            />
          </div>
          <div class="create-team-image" :key="i" v-for="(image, i) in imageList">
            <button
              class="team-image-button btn btn-default"
              @click="chooseImage(image)"
              :class="{ active: chosenImage == image }"
              data-test="team-image"
              type="button"
            >
              <cms-img :src="image"></cms-img>
            </button>
          </div>
        </div>
      </div>

      <div class="text-center">
        <div style="display: flex; justify-content: center; margin-bottom: 4rem">
          <button class="btn btn-default" @click="cancel" type="button">Cancel</button>
          <button
            class="btn btn-primary"
            @click="createTeam"
            style="margin-left: 20px"
            data-test="submit-new-team"
            type="button"
          >
            Create
          </button>
        </div>
        <p class="text-danger" v-if="error" data-test="submit-team-error">{{ error }}</p>
      </div>
    </div>
  </main>
</template>

<script setup lang="ts">
import { maxLength, required } from "@vuelidate/validators";
import { debug as d } from "debug";
import axios, { AxiosError } from "axios";
import { computed, onMounted, ref } from "vue";
import useVuelidate from "@vuelidate/core";
import InfoIcon from "../../components/info-icon.vue";
import StepNumber from "../../components/step-number.vue";
import DataTag from "../../components/data-tag.vue";
import ImageUploader from "../../components/image-uploader.vue";
import CreateTeamModal from "../../components/team/create-team-modal.vue";
import cmsContentMap from "../../services/cms/cms-content-map";
import { CmsImage, CmsTeamImageList } from "../../services/cms/cms-type-definitions";
import cms from "../../services/cms/cms";
import { TeamApiModel, UserTeamsTagDataApiModel } from "../../api-models";
import CmsImg from "../../components/cms/cms-img.vue";

const debug = d("cs.CreateTeam");

const teamNameRegex = /^[\w (),'-]*$/;

const id = ref("");
const name = ref("");
const userTeamTags = ref<UserTeamsTagDataApiModel | null>(null);
const uploadedFile = ref<File | null>(null);
const newTeam = ref<TeamApiModel | null>(null);
const customImageIsSelected = ref(false);
const error = ref("");
const showModal = ref(false);
const imageListDoc = ref<CmsTeamImageList | null>(null);
const chosenImage = ref<CmsImage | null>(null);
const isCreating = ref(false);

const rules = {
  name: { required, maxLength: maxLength(80), regex: (value: string) => teamNameRegex.test(value) },
};

const state = {
  name,
};

const v$ = useVuelidate(rules, state, { $autoDirty: true });

function selectCustomImage(): void {
  customImageIsSelected.value = true;
  chosenImage.value = null;
}

function onFileUpload(file: File) {
  uploadedFile.value = file;
  selectCustomImage();
}

const userTaggedTeams = computed(() => userTeamTags.value?.taggedTeams);

function fetchUserTeamTags() {
  axios
    .get("/api/usertagdata/getteamdata")
    .then((resp) => {
      userTeamTags.value = resp.data;
    })
    .catch((err) => {
      debug(err);
      // do nothing - not a showstopper
    });
}

async function submitWithCustomImage(file: File): Promise<void> {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("teamName", name.value);

  try {
    const response = await axios.post<TeamApiModel>("/api/teams/CreateWithImage", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    debug("Upload successful");

    newTeam.value = response.data;
    showModal.value = true;
  } catch (e) {
    const axiosError = e as AxiosError;
    error.value = axiosError?.response?.data ?? axiosError.message;
    isCreating.value = false;
  }
}

onMounted(() => {
  cms.getById("cms-team-image-list", cmsContentMap.settings.teamImageListId).then((doc) => {
    imageListDoc.value = doc;
    chosenImage.value = imageListDoc.value.data.list[0].image;
  });
  fetchUserTeamTags();
});

const imageList = computed(() => imageListDoc.value?.data.list.map((l) => l.image));

function chooseImage(image: CmsImage): void {
  chosenImage.value = image;
  customImageIsSelected.value = false;
}

function cancel(): void {
  window.location.href = "/";
}

function submitWithChosenImage(): void {
  const apiModel = {
    id: id.value,
    name: name.value,
    imageUrl: chosenImage.value?.url,
  } as TeamApiModel;

  axios
    .post("/api/teams/Create", apiModel)
    .then((resp) => {
      newTeam.value = resp.data as TeamApiModel;
      showModal.value = true;
    })
    .catch((err) => {
      debug(err);
      error.value = "There was a problem creating your team. Please try again.";
      isCreating.value = false;
    });
}

async function createTeam(): Promise<void> {
  if (isCreating.value) {
    return;
  }

  if (v$.value.$touch) {
    v$.value.$touch();
  } else {
    throw new Error("Vuelidate this.$v.$touch() unavailable");
  }

  error.value = "";
  if (v$.value.$invalid) {
    error.value = "Please enter a valid team name.";
    return;
  }

  isCreating.value = true;
  if (uploadedFile.value && customImageIsSelected.value) {
    await submitWithCustomImage(uploadedFile.value);
  } else {
    submitWithChosenImage();
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="less">
@import (reference) "../../styles/bootstrap/variables.less";

.error-feedback {
  display: none;
}

.has-error .error-feedback {
  display: block;
}

.btn.team-image-button {
  width: 100%;
  padding: 0;
  border-radius: 2px;
}

.image-grid {
  margin: 20px 0;
}

.user-invite-sender-input {
  max-width: 567px !important; //Fix for IE11
}

.create-team-image {
  width: 25%;
  padding: 5px;
  display: inline-block;

  button:focus {
    outline: none !important;
  }

  @media (max-width: @screen-xs-max) {
    width: 33.33%;
  }

  @media (max-width: @screen-xxs-max) {
    width: 50%;
  }
}

.create-team-image {
  .btn {
    padding: 0;
  }

  .btn.active {
    border-color: #ed0b6f !important;
    border-width: 2px;
    margin: -1px;
  }
}

.shadow-tile {
  box-shadow: 0px 0px 5px black;
}

.submit-button-wrapper {
  padding: 5px;
  margin-bottom: 50px;
}

// ----------- new styles -----------
@input-height: 40px;
@input-padding: 5px;

.create-team-page {
  h2 {
    word-wrap: break-word;
    font-size: 24px;
  }
}

.create-team-section {
  margin-bottom: 3rem;
}

.section-title {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  font-size: 24px;

  h2 {
    margin: 0 0 0 10px;
  }
}

.team-create-description {
  font-size: 16px;
  color: @off-black;
}

.team-input {
  background-color: #fff;
  border: 1px #ddd solid;
  width: 60%;
  min-width: 300px;
  max-width: 60%;
  padding: 0 @input-padding;
  margin-bottom: @input-padding;
  resize: none;
  overflow: hidden;
  height: @input-height;

  &::placeholder {
    color: @form-text-color;
  }

  @media (max-width: @screen-xs-max) {
    width: 100%;
    max-width: 100%;
    min-width: unset;
  }
}

.tagged-teams-container,
.data-tags {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 5px;
}

.tagged-teams-container {
  margin-top: 10px;
}

.data-tags {
  width: 100%;
}

/* Hide scrollbar for Chrome, Safari and Opera */
.data-tags::-webkit-scrollbar {
  display: none;
}
</style>
